import React, { useContext, useState, memo, useEffect } from 'react';
import { i18nContext } from './context/i18n.context';
import { stepContext } from './context/step.context';
import { displayContext } from './context/display.context';
import { UserContext } from './context/user.context';
import { useRouter } from 'next/router';
import omit from 'lodash/omit';
import { STATUS_STARTED } from '@/components/pages/assessments/util';

const AssessmentChooseClient = ({ startAssessment }) => {
  const router = useRouter();
  const { t } = useContext(i18nContext);
  const { css } = useContext(displayContext);
  const { components, slug, metadata, status } = useContext(stepContext);
  const { agencyClient, setAgencyClient, isAgencyClientRetrieved } = useContext(UserContext);
  const [radioState, setRadioState] = useState(!!agencyClient ? 'client' : 'self');
  const [disableOpenModalButton, setDisableOpenModalButton] = useState(false);
  // the agencyClient from context is the actual client, and will be set to null when answering as "self"
  // but selectedClient will remember the selected client when toggling between the self and client options.
  const [selectedClient, setSelectedClient] = useState(null);
  // track if client was passed via URL parameter (not manually selected) to hide change client UI
  const [clientFromUrlParam, setClientFromUrlParam] = useState(false);

  // selectedClient will be null on initial page load, even if we load agencyClient from a url parameter,
  // since that happens in an api call
  const currentClient = selectedClient || agencyClient;

  // there can be a delay in opening the modal, so we can just ensure the button click doesn't
  // queue more than one instance in a short period of time
  useEffect(() => {
    if (disableOpenModalButton) {
      setTimeout(() => setDisableOpenModalButton(false), 1000);
    }
  }, [disableOpenModalButton]);

  // Check if client was passed via URL parameter on initial load ONLY
  // This flag should only be set once when the component mounts with a client in the URL
  useEffect(() => {
    if (router.query.client && isAgencyClientRetrieved && agencyClient && !selectedClient && !clientFromUrlParam) {
      // Only set to true if we have a client from URL and user hasn't manually selected one yet
      setClientFromUrlParam(true);
    }
  }, [router.query.client, isAgencyClientRetrieved, agencyClient]);

  const PrimaryButton = memo(components().PrimaryButton);

  const takeAs = (val) => {
    setRadioState(val);
    if (val === 'client') {
      if (!currentClient) openClientModal();
      setAgencyClient(currentClient);
    } else {
      setAgencyClient(null);
      router.replace({ pathname: router.pathname, query: { ...omit(router.query, 'client') } }, undefined, { shallow: true });
    }
  };

  const openClientModal = () => {
    if (disableOpenModalButton) return;
    setDisableOpenModalButton(true);
    components().prompts.modal.choose_client({
      done: (modal, client) => {
        if (client) {
          setAgencyClient(client);
          setSelectedClient(client);
          // When user manually selects a client, it's NOT from URL param
          setClientFromUrlParam(false);
        } else {
          setAgencyClient(null);
        }
        // replace the client url parameter with the new id
        router.replace({ pathname: router.pathname, query: { ...router.query, client: client.id, all: slug, startNew: 'true' } });
        modal.close();
      }
    });
  };

  // TODO for future assessments - PEARLS doesn't really have a use case for supporters to take as themselves
  // const answerForMe = () => {
  //   setAgencyClient(null);
  //   startAssessment();
  // };

  const btns = metadata.ui?.buttons || {};

  const continueButton = (() => {
    const clientFirstName = agencyClient?.first_name || agencyClient?.last_name || ''; // default to last name if needed
    const label = agencyClient
      ? t(`${STATUS_STARTED == status ? 'Continue' : 'Take'} assessment as client`, { clientFirstName })
      : t(btns.start || 'Continue');
    const btnProps = { styles: { width: 'full' } };
    return (
      <PrimaryButton classes={css('action-btn')} disabled={radioState === 'client' && !agencyClient} click={startAssessment} {...btnProps}>
        {label}
      </PrimaryButton>
    );
  })();

  const clearAndOpenModal = () => {
    setSelectedClient(null);
    openClientModal();
  };

  // wait for word that any relevant api calls have finished
  if (!isAgencyClientRetrieved) return null;

  return (
    <div className={css('assessment-choose-client')}>
      {!currentClient && (
        <div>
          <h5>{t('Are you answering for yourself or on behalf of someone else?')}</h5>
          <div>
            <label className={css('pointer-btn')}>
              <input type="radio" name="taking-assessment-as" checked={radioState === 'self'} onClick={() => takeAs('self')} />
              &nbsp;
              {t('This is for myself')}
            </label>
          </div>
          <div>
            <label className={css('pointer-btn')}>
              <input type="radio" name="taking-assessment-as" checked={radioState === 'client'} onClick={() => takeAs('client')} />
              &nbsp;
              {currentClient ? t('Take assessment as client', { clientFirstName: currentClient.first_name }) : t('This is for someone else')}
              {currentClient && (
                <span>
                  &nbsp;&mdash;&nbsp;
                  <button className={css('link-btn')} onClick={openClientModal}>
                    {t('Take assessment as someone else')}
                  </button>
                </span>
              )}
            </label>
          </div>
        </div>
      )}
      <div>{continueButton}</div>
      {currentClient && !clientFromUrlParam && (
        <>
          <div className={css('start-as-someone-else')}>
            <button onClick={clearAndOpenModal} className={css('link-btn')}>
              {t('Start again for someone else')}
            </button>
          </div>
          <div className={css('start-as-someone-else')}>
            <button
              onClick={() => {
                setSelectedClient(null);
                takeAs('self');
              }}
              className={css('link-btn')}
            >
              {t('Start again as myself')}
            </button>
          </div>
        </>
      )}
    </div>
  );
};

export default AssessmentChooseClient;
